/**
 * Location preference options for the app UI.
 *
 * These are ordered to match the finalized state-first alphabetical output used in the
 * onboarding/settings flows. Some options map to Ticketmaster supported markets, while
 * others fall back to a state-only preference when there is no supported market grouping.
 */

const TICKETMASTER_MARKETS = [
  { id: 'AK-50', name: 'AK - Alaska', marketId: '50', primaryState: 'AK' },
  { id: 'AL-1', name: 'AL - Birmingham & surrounding areas', marketId: '1', primaryState: 'AL' },
  {
    id: 'AR-29',
    name: 'AR - Memphis, Little Rock & surrounding areas',
    marketId: '29',
    primaryState: 'AR',
  },
  { id: 'AZ-36', name: 'AZ - Phoenix & Tucson', marketId: '36', primaryState: 'AZ' },
  { id: 'CA-27', name: 'CA - Greater Los Angeles Area', marketId: '27', primaryState: 'CA' },
  { id: 'CA-20', name: 'CA - Greater San Diego Area', marketId: '20', primaryState: 'CA' },
  { id: 'CA-41', name: 'CA - N. California/N. Nevada', marketId: '41', primaryState: 'CA' },
  { id: 'CO-6', name: 'CO - Denver & surrounding areas', marketId: '6', primaryState: 'CO' },
  { id: 'CT-124', name: 'CT - Connecticut', marketId: '124', primaryState: 'CT' },
  { id: 'CT-35', name: 'CT - New York/Tri-State Area', marketId: '35', primaryState: 'CT' },
  { id: 'DC-47', name: 'DC - Washington, DC and Maryland', marketId: '47', primaryState: 'DC' },
  { id: 'DE-state', name: 'DE - Delaware', marketId: null, primaryState: 'DE' },
  { id: 'FL-15', name: 'FL - Greater Miami Area', marketId: '15', primaryState: 'FL' },
  { id: 'FL-17', name: 'FL - Greater Orlando Area', marketId: '17', primaryState: 'FL' },
  { id: 'FL-21', name: 'FL - Greater Tampa Area', marketId: '21', primaryState: 'FL' },
  {
    id: 'FL-25',
    name: 'FL - Jacksonville & surrounding areas',
    marketId: '25',
    primaryState: 'FL',
  },
  { id: 'GA-10', name: 'GA - Greater Atlanta Area', marketId: '10', primaryState: 'GA' },
  { id: 'HI-49', name: 'HI - Hawaii', marketId: '49', primaryState: 'HI' },
  { id: 'IA-24', name: 'IA - Iowa', marketId: '24', primaryState: 'IA' },
  { id: 'ID-state', name: 'ID - Idaho', marketId: null, primaryState: 'ID' },
  { id: 'IL-54', name: 'IL - Central Illinois', marketId: '54', primaryState: 'IL' },
  { id: 'IL-3', name: 'IL - Chicagoland & Northern IL', marketId: '3', primaryState: 'IL' },
  { id: 'IL-53', name: 'IL - Springfield', marketId: '53', primaryState: 'IL' },
  {
    id: 'IN-23',
    name: 'IN - Indianapolis & surrounding areas',
    marketId: '23',
    primaryState: 'IN',
  },
  { id: 'KS-26', name: 'KS - Kansas City & surrounding areas', marketId: '26', primaryState: 'KS' },
  { id: 'KY-28', name: 'KY - Louisville & Lexington', marketId: '28', primaryState: 'KY' },
  { id: 'LA-34', name: 'LA - New Orleans & surrounding areas', marketId: '34', primaryState: 'LA' },
  { id: 'MA-11', name: 'MA - Greater Boston Area', marketId: '11', primaryState: 'MA' },
  { id: 'MA-33', name: 'MA - New England', marketId: '33', primaryState: 'MA' },
  { id: 'MD-47', name: 'MD - Washington, DC and Maryland', marketId: '47', primaryState: 'MD' },
  { id: 'ME-33', name: 'ME - New England', marketId: '33', primaryState: 'ME' },
  {
    id: 'MI-7',
    name: 'MI - Detroit, Toledo & surrounding areas',
    marketId: '7',
    primaryState: 'MI',
  },
  { id: 'MI-9', name: 'MI - Grand Rapids & surrounding areas', marketId: '9', primaryState: 'MI' },
  {
    id: 'MN-16',
    name: 'MN - Minneapolis/St. Paul & surrounding areas',
    marketId: '16',
    primaryState: 'MN',
  },
  { id: 'MO-26', name: 'MO - Kansas City & surrounding areas', marketId: '26', primaryState: 'MO' },
  { id: 'MO-39', name: 'MO - St. Louis & surrounding areas', marketId: '39', primaryState: 'MO' },
  { id: 'MS-state', name: 'MS - Mississippi', marketId: null, primaryState: 'MS' },
  { id: 'MT-45', name: 'MT - Utah & Montana', marketId: '45', primaryState: 'MT' },
  { id: 'NC-2', name: 'NC - Charlotte', marketId: '2', primaryState: 'NC' },
  { id: 'NC-38', name: 'NC - Raleigh & Durham', marketId: '38', primaryState: 'NC' },
  { id: 'ND-43', name: 'ND - North & South Dakota', marketId: '43', primaryState: 'ND' },
  { id: 'NE-52', name: 'NE - Nebraska', marketId: '52', primaryState: 'NE' },
  { id: 'NH-33', name: 'NH - New England', marketId: '33', primaryState: 'NH' },
  { id: 'NJ-35', name: 'NJ - New York/Tri-State Area', marketId: '35', primaryState: 'NJ' },
  { id: 'NJ-55', name: 'NJ - Northern New Jersey', marketId: '55', primaryState: 'NJ' },
  { id: 'NM-8', name: 'NM - El Paso & New Mexico', marketId: '8', primaryState: 'NM' },
  { id: 'NV-14', name: 'NV - Greater Las Vegas Area', marketId: '14', primaryState: 'NV' },
  { id: 'NV-41', name: 'NV - N. California/N. Nevada', marketId: '41', primaryState: 'NV' },
  { id: 'NY-35', name: 'NY - New York/Tri-State Area', marketId: '35', primaryState: 'NY' },
  { id: 'NY-44', name: 'NY - Upstate New York', marketId: '44', primaryState: 'NY' },
  { id: 'OH-4', name: 'OH - Cincinnati & Dayton', marketId: '4', primaryState: 'OH' },
  {
    id: 'OH-12',
    name: 'OH - Cleveland, Youngstown & surrounding areas',
    marketId: '12',
    primaryState: 'OH',
  },
  {
    id: 'OH-7',
    name: 'OH - Detroit, Toledo & surrounding areas',
    marketId: '7',
    primaryState: 'OH',
  },
  { id: 'OH-13', name: 'OH - Greater Columbus Area', marketId: '13', primaryState: 'OH' },
  { id: 'OK-125', name: 'OK - Oklahoma', marketId: '125', primaryState: 'OK' },
  { id: 'OR-37', name: 'OR - Portland & surrounding areas', marketId: '37', primaryState: 'OR' },
  { id: 'PA-18', name: 'PA - Greater Philadelphia Area', marketId: '18', primaryState: 'PA' },
  { id: 'PA-19', name: 'PA - Greater Pittsburgh Area', marketId: '19', primaryState: 'PA' },
  { id: 'RI-33', name: 'RI - New England', marketId: '33', primaryState: 'RI' },
  { id: 'SC-121', name: 'SC - South Carolina', marketId: '121', primaryState: 'SC' },
  { id: 'SD-43', name: 'SD - North & South Dakota', marketId: '43', primaryState: 'SD' },
  {
    id: 'TN-29',
    name: 'TN - Memphis, Little Rock & surrounding areas',
    marketId: '29',
    primaryState: 'TN',
  },
  {
    id: 'TN-31',
    name: 'TN - Nashville, Knoxville & surrounding areas',
    marketId: '31',
    primaryState: 'TN',
  },
  { id: 'TX-123', name: 'TX - Beaumont', marketId: '123', primaryState: 'TX' },
  {
    id: 'TX-5',
    name: 'TX - Dallas - Fort Worth & surrounding areas',
    marketId: '5',
    primaryState: 'TX',
  },
  { id: 'TX-8', name: 'TX - El Paso & New Mexico', marketId: '8', primaryState: 'TX' },
  { id: 'TX-22', name: 'TX - Houston & surrounding areas', marketId: '22', primaryState: 'TX' },
  { id: 'TX-40', name: 'TX - San Antonio & Austin', marketId: '40', primaryState: 'TX' },
  { id: 'TX-122', name: 'TX - South Texas', marketId: '122', primaryState: 'TX' },
  { id: 'UT-45', name: 'UT - Utah & Montana', marketId: '45', primaryState: 'UT' },
  { id: 'VA-46', name: 'VA - Virginia', marketId: '46', primaryState: 'VA' },
  { id: 'VT-33', name: 'VT - New England', marketId: '33', primaryState: 'VT' },
  { id: 'WA-42', name: 'WA - Greater Seattle Area', marketId: '42', primaryState: 'WA' },
  { id: 'WI-30', name: 'WI - Milwaukee & WI', marketId: '30', primaryState: 'WI' },
  { id: 'WV-48', name: 'WV - West Virginia', marketId: '48', primaryState: 'WV' },
  { id: 'WY-state', name: 'WY - Wyoming', marketId: null, primaryState: 'WY' },
];

function getMarketById(optionId) {
  return (
    TICKETMASTER_MARKETS.find(m => m.id === String(optionId)) ||
    TICKETMASTER_MARKETS.find(m => m.marketId === String(optionId)) ||
    null
  );
}

function getMarketLabel(optionId) {
  const market = getMarketById(optionId);
  return market ? market.name : '';
}

function getLocationPreferenceSelection(optionId) {
  return TICKETMASTER_MARKETS.find(option => option.id === String(optionId)) || null;
}

function getLocationPreferencePayload(optionId) {
  const option = getLocationPreferenceSelection(optionId);

  return {
    marketId: option?.marketId || null,
    primary_city: null,
    primary_state: option?.primaryState || null,
  };
}

function getLocationPreferenceOptionId(locationPreferences = {}) {
  const selectedOptionId = locationPreferences.selectionId || locationPreferences.optionId;
  if (selectedOptionId && getLocationPreferenceSelection(selectedOptionId)) {
    return selectedOptionId;
  }

  const marketId = locationPreferences.marketId ? String(locationPreferences.marketId) : null;
  const primaryState = locationPreferences.primary_state || null;

  if (marketId && primaryState) {
    const exactMatch = TICKETMASTER_MARKETS.find(
      option => option.marketId === marketId && option.primaryState === primaryState
    );
    if (exactMatch) {
      return exactMatch.id;
    }
  }

  if (marketId) {
    const marketMatch = TICKETMASTER_MARKETS.find(option => option.marketId === marketId);
    if (marketMatch) {
      return marketMatch.id;
    }
  }

  if (primaryState) {
    const stateMatch = TICKETMASTER_MARKETS.find(
      option => !option.marketId && option.primaryState === primaryState
    );
    if (stateMatch) {
      return stateMatch.id;
    }
  }

  return '';
}

export {
  TICKETMASTER_MARKETS,
  getMarketById,
  getMarketLabel,
  getLocationPreferenceOptionId,
  getLocationPreferencePayload,
  getLocationPreferenceSelection,
};

export default TICKETMASTER_MARKETS;
